import type { ColorPalette } from "./types";

export interface ContrastPairResult {
  label: string;
  foreground: string;
  background: string;
  ratio: number;
  requiredRatio: number;
  passed: boolean;
}

export interface PaletteContrastValidation {
  passed: boolean;
  score: number; // Percentage of passing checks (0 - 100)
  pairs: ContrastPairResult[];
  failures: string[];
}

/**
 * Normalizes and parses a 3, 4, 6, or 8 digit hex color code to RGB components [0..255].
 */
export function parseHexToRgb(hex: string): [number, number, number] | null {
  if (!hex || typeof hex !== "string") return null;

  let clean = hex.trim().replace(/^#/, "");

  // Strip RGBA or RRGGBBAA alpha
  if (clean.length === 4) {
    clean = clean.slice(0, 3);
  } else if (clean.length === 8) {
    clean = clean.slice(0, 6);
  }

  if (clean.length === 3) {
    clean = clean
      .split("")
      .map((c) => c + c)
      .join("");
  }

  if (clean.length !== 6) return null;

  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;

  return [r, g, b];
}

/**
 * Calculates WCAG 2.1 relative luminance for an sRGB color.
 * Normalized to [0..1] range.
 */
export function getRelativeLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((val) => {
    const s = val / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the exact WCAG contrast ratio between two hex colors.
 * Formula: (L1 + 0.05) / (L2 + 0.05) where L1 is lighter.
 * Returns a value between 1.0 and 21.0.
 */
export function getContrastRatio(colorA: string, colorB: string): number {
  const rgbA = parseHexToRgb(colorA);
  const rgbB = parseHexToRgb(colorB);

  if (!rgbA || !rgbB) return 1.0;

  const lumA = getRelativeLuminance(rgbA);
  const lumB = getRelativeLuminance(rgbB);

  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);

  const ratio = (lighter + 0.05) / (darker + 0.05);
  return Math.round(ratio * 100) / 100;
}

/**
 * Selects an accessible foreground color (dark or light) against a given background.
 */
export function getAccessibleForeground(
  backgroundHex: string,
  preferredDark = "#0f172a",
  preferredLight = "#ffffff"
): string {
  const darkRatio = getContrastRatio(preferredDark, backgroundHex);
  const lightRatio = getContrastRatio(preferredLight, backgroundHex);

  return lightRatio >= darkRatio ? preferredLight : preferredDark;
}

/**
 * Checks if two colors meet a given minimum contrast threshold (e.g. 4.5 for AA normal text, 3.0 for large/UI).
 */
export function isAccessibleContrast(colorA: string, colorB: string, minRatio = 4.5): boolean {
  return getContrastRatio(colorA, colorB) >= minRatio;
}

/**
 * Validates all standard semantic contrast pairs for a ColorPalette against WCAG AA standards.
 */
export function validatePaletteContrast(palette: ColorPalette): PaletteContrastValidation {
  const pairsToCheck: Array<{
    label: string;
    fg: string;
    bg: string;
    required: number;
  }> = [
    {
      label: "Body Text on Canvas",
      fg: palette.foreground,
      bg: palette.background,
      required: 4.5,
    },
    {
      label: "Primary Button Text",
      fg: palette.primaryForeground,
      bg: palette.primary,
      required: 4.5,
    },
    {
      label: "Secondary Button Text",
      fg: palette.secondaryForeground,
      bg: palette.secondary,
      required: 4.5,
    },
    {
      label: "Accent Foreground",
      fg: palette.accentForeground,
      bg: palette.accent,
      required: 4.5,
    },
    {
      label: "Strong Text on Canvas",
      fg: palette.textStrong || palette.foreground,
      bg: palette.background,
      required: 4.5,
    },
    {
      label: "Muted Text on Canvas",
      fg: palette.textMuted || palette.mutedForeground,
      bg: palette.background,
      required: 3.0,
    },
    {
      label: "Muted Pill Text on Muted Surface",
      fg: palette.mutedForeground,
      bg: palette.muted,
      required: 3.0,
    },
  ];

  const pairs: ContrastPairResult[] = pairsToCheck.map((item) => {
    const ratio = getContrastRatio(item.fg, item.bg);
    const passed = ratio >= item.required;
    return {
      label: item.label,
      foreground: item.fg,
      background: item.bg,
      ratio,
      requiredRatio: item.required,
      passed,
    };
  });

  const failures = pairs
    .filter((p) => !p.passed)
    .map((p) => `${p.label} ratio ${p.ratio}:1 fails required ${p.requiredRatio}:1 (${p.foreground} on ${p.background})`);

  const passedCount = pairs.filter((p) => p.passed).length;
  const score = Math.round((passedCount / pairs.length) * 100);

  return {
    passed: failures.length === 0,
    score,
    pairs,
    failures,
  };
}
